// Shared helpers for resolving who completed / paused a task or checklist item,
// including SYSTEM attribution for automated (no-user) actions.
//
// Background: completion / pause can be performed either by a practice user or
// by an automated/system process (e.g. an auto-resume, a workflow-driven
// completion). When there's no user actor, the BE leaves `completed_by` /
// `completed_by_practice_user` null. The UI must not render a blank or an
// em-dash for those — it shows "System" so the action is still attributed.

// Method/source values that indicate an automated, non-user action. Compared
// case-insensitively against `completion_method` / `paused_method` / `source`.
const SYSTEM_METHOD_HINTS = ['system', 'automation', 'automated', 'auto', 'workflow', 'scheduler', 'cron'];

function isSystemMethod(value) {
    if (!value || typeof value !== 'string') return false;
    const v = value.toLowerCase();
    return SYSTEM_METHOD_HINTS.some((h) => v.includes(h));
}

// Resolve a human display name from a practice-user-ish object / string.
function resolveActorName(actor, stringFallback) {
    if (typeof stringFallback === 'string' && stringFallback.trim()) return stringFallback.trim();
    if (!actor) return '';
    if (typeof actor === 'string') return actor.trim();
    if (typeof actor === 'object') {
        return actor.full_name
            || `${actor.first_name || ''} ${actor.last_name || ''}`.trim()
            || actor.email
            || '';
    }
    return '';
}

/**
 * Resolve completer attribution for a task row.
 * Returns { name, avatarUrl, isSystem }.
 *
 * - A real user name wins.
 * - When no user is present but the row is completed (or a system method is
 *   flagged), returns { name: 'System', isSystem: true }.
 * - Otherwise name is '' (caller can choose to render nothing).
 */
// Pull an avatar/photo URL off a user-ish object. The app stores it under
// `.avatar` in most places (assignees, activities), `.avatar_url` on some user
// payloads, and `.profile_pic` on others — check all three.
function resolveAvatarUrl(actor, task) {
    return actor?.avatar
        || actor?.avatar_url
        || actor?.profile_pic
        || task?.completed_by_avatar_url
        || undefined;
}

export function resolveCompleter(task) {
    if (!task) return { name: '', avatarUrl: undefined, isSystem: false };
    const actor = task.completed_by_practice_user || (typeof task.completed_by === 'object' ? task.completed_by : null);
    const name = resolveActorName(actor, null)
        || task.completed_by_full_name
        || resolveActorName(task.completed_by, typeof task.completed_by === 'string' ? task.completed_by : null);
    const avatarUrl = resolveAvatarUrl(actor, task);
    if (name) return { name, avatarUrl, isSystem: false };

    // No user actor. If the task is completed (or an explicit system method is
    // set), attribute to System rather than leaving it blank.
    const completed = task.status === 'completed' || task.status === 'archived' || task.completed_at != null;
    const systemFlagged = isSystemMethod(task.completion_method) || isSystemMethod(task.completed_via) || isSystemMethod(task.source);
    if (completed || systemFlagged) return { name: 'System', avatarUrl: undefined, isSystem: true };
    return { name: '', avatarUrl: undefined, isSystem: false };
}

/**
 * Resolve pauser attribution for a paused checklist item.
 * Returns { name, isSystem }. Falls back to 'System' when paused with no user.
 */
export function resolvePauser(item) {
    if (!item) return { name: '', avatarUrl: undefined, isSystem: false };
    const actor = item.completed_by_practice_user
        || item.paused_by_practice_user
        || (typeof item.paused_by === 'object' ? item.paused_by : null)
        || (typeof item.completed_by === 'object' ? item.completed_by : null)
        || null;
    let stringActorName = null;
    if (typeof item.completed_by === 'string') stringActorName = item.completed_by;
    else if (typeof item.paused_by === 'string') stringActorName = item.paused_by;
    const name = resolveActorName(actor, stringActorName)
        || item.paused_by_full_name
        || item.completed_by_full_name
        || '';
    const avatarUrl = actor?.avatar || actor?.avatar_url || actor?.profile_pic || undefined;
    if (name) return { name, avatarUrl, isSystem: false };
    // Paused with no user actor → System (the pause must have been automated).
    return { name: 'System', avatarUrl: undefined, isSystem: true };
}
